import { motion } from "framer-motion";
import { Users, Gift, ArrowRight, Mail, MessageCircle } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { scrollToId } from "./Nav";

const shareLinks = (r) => {
  const url = window.location.origin;
  return {
    whatsapp: `https://wa.me/?text=${encodeURIComponent(`${r.shareText} ${url}`)}`,
    email: `mailto:?subject=${encodeURIComponent(r.shareSubject)}&body=${encodeURIComponent(`${r.shareText}\n${url}`)}`,
  };
};

const fade = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
};

export const Referral = () => {
  const { t } = useLang();
  const r = t.referral;

  return (
    <section id="referral" className="relative py-24 md:py-32 border-y border-white/10 bg-[#0d0d0d] overflow-hidden" data-testid="referral-section">
      <div aria-hidden className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-[#FF7900]/10 blur-3xl" />
      <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
        <motion.p {...fade} transition={{ duration: 0.6 }} className="text-xs uppercase tracking-[0.3em] text-[#FF7900] mb-4">
          {r.overline}
        </motion.p>
        <motion.h2 {...fade} transition={{ duration: 0.6, delay: 0.05 }} className="font-display text-base md:text-lg font-semibold max-w-2xl mb-6">
          {r.title}
        </motion.h2>
        <motion.p {...fade} transition={{ duration: 0.6, delay: 0.1 }} className="text-white/60 text-sm md:text-base max-w-2xl mb-12">
          {r.text}
        </motion.p>

        <div className="grid md:grid-cols-2 gap-4 max-w-3xl mb-12">
          <motion.div {...fade} transition={{ duration: 0.6, delay: 0.15 }} className="border border-white/10 bg-[#111111] p-8" data-testid="referral-card-5">
            <Users className="w-6 h-6 text-[#FF7900] mb-5" />
            <p className="font-display text-5xl md:text-6xl font-medium text-white">-5%</p>
            <p className="text-sm text-white/50 mt-3">{r.per}</p>
          </motion.div>
          <motion.div {...fade} transition={{ duration: 0.6, delay: 0.25 }} className="border border-[#FF7900]/40 bg-[#FF7900]/[0.06] p-8" data-testid="referral-card-100">
            <Gift className="w-6 h-6 text-[#FF7900] mb-5" />
            <p className="font-display text-5xl md:text-6xl font-medium text-[#FF7900]">-100%</p>
            <p className="text-sm text-white/50 mt-3">{r.max}</p>
          </motion.div>
        </div>

        <motion.div {...fade} transition={{ duration: 0.6, delay: 0.3 }}>
          <p className="text-white font-semibold mb-6">{r.highlight}</p>
          <div className="flex flex-wrap items-center gap-4">
            <button
              onClick={() => scrollToId("contact")}
              data-testid="referral-cta-button"
              className="inline-flex items-center gap-2 bg-[#FF7900] hover:bg-white hover:text-black text-white text-sm font-semibold px-6 py-3 rounded-full transition-colors duration-300"
            >
              {r.cta}
              <ArrowRight className="w-4 h-4" />
            </button>
            <span className="text-sm text-white/40">{r.shareLabel}</span>
            <a
              href={shareLinks(r).whatsapp}
              target="_blank"
              rel="noreferrer"
              data-testid="share-whatsapp-button"
              aria-label={r.shareWhatsapp}
              className="inline-flex items-center gap-2 border border-[#25D366]/50 text-[#25D366] hover:bg-[#25D366] hover:text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors duration-300"
            >
              <MessageCircle className="w-4 h-4" />
              WhatsApp
            </a>
            <a
              href={shareLinks(r).email}
              data-testid="share-email-button"
              aria-label={r.shareEmail}
              className="inline-flex items-center gap-2 border border-white/25 text-white/70 hover:border-[#FF7900] hover:text-[#FF7900] text-sm font-semibold px-5 py-2.5 rounded-full transition-colors duration-300"
            >
              <Mail className="w-4 h-4" />
              E-mail
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
